import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { _, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TooltipModule } from 'primeng/tooltip';
import { Task } from '../../shared/models/task.model';
import { TaskService } from '../../shared/services/task.service';
import { UserService } from '../../shared/services/user.service';
import {
  getDueSoonWindowMs,
  getPriorityLabelKey,
  getPrioritySeverity,
  getRecurrenceLabelKey,
  isTaskFinished,
} from './task-display';

const TZ = 'Europe/Berlin';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type StatusTab = 'open' | 'done' | 'all';

type GroupKey =
  | 'overdue'
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'later'
  | 'noDue'
  | 'doneToday'
  | 'finished';

const GROUP_ORDER: GroupKey[] = [
  'overdue',
  'today',
  'tomorrow',
  'thisWeek',
  'later',
  'noDue',
  'doneToday',
  'finished',
];

export interface TaskGroup {
  key: GroupKey;
  tasks: Task[];
}

/** Display text for the due date, already split into translation key + params. */
export interface DueLabel {
  key: string | null;
  params: Record<string, string>;
  text: string;
}

@Component({
  selector: 'app-tasks-page',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AvatarModule,
    BadgeModule,
    TagModule,
    ToastModule,
    ButtonModule,
    SelectButtonModule,
    ToggleButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TooltipModule,
  ],
  providers: [
    MessageService,
    TaskService,
    ConfirmationService,
    TranslateService,
  ],
  templateUrl: './tasks-page.component.html',
})
export class TasksPageComponent implements OnInit {
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #router = inject(Router);
  #locale = inject(LOCALE_ID);
  // Stored as a string in the login payload.
  #currentUserId = inject(UserService).getUser()?.id || null;
  taskService = inject(TaskService);

  readonly priorityChips = ['all', 'critical', 'high', 'medium', 'low'];
  readonly tabs: StatusTab[] = ['open', 'done', 'all'];

  search = signal('');
  statusTab = signal<StatusTab>('open');
  onlyMine = signal(true);
  priority = signal<string>('all');
  /** Ticks every minute so "today" / "overdue" stay correct on an open page. */
  #now = signal(Date.now());

  readonly priorityLabelKey = getPriorityLabelKey;
  readonly prioritySeverity = getPrioritySeverity;
  readonly recurrenceLabelKey = getRecurrenceLabelKey;

  /**
   * One row per execution (see TaskService.getExecutionRows), after search,
   * "mine" and priority — the base for counts and tabs.
   */
  filteredTasks = computed(() => {
    const onlyMine = this.onlyMine() && this.#currentUserId !== null;
    const priority = this.priority();
    const term = this.search().trim().toLowerCase();

    return this.taskService.tasks().filter((task) => {
      // "Mine" includes unassigned tasks: those are meant for everybody.
      if (
        onlyMine &&
        task.responsible_user &&
        String(task.responsible_user.id) !== String(this.#currentUserId)
      ) {
        return false;
      }
      if (priority !== 'all' && task.priority !== priority) {
        return false;
      }
      if (term) {
        const haystack = [
          task.title,
          task.description,
          task.responsible_user?.name ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  });

  counts = computed(() => {
    const tasks = this.filteredTasks();
    const done = tasks.filter((t) => isTaskFinished(t)).length;
    return {
      open: tasks.length - done,
      done,
      all: tasks.length,
      overdue: tasks.filter((t) => !isTaskFinished(t) && this.isOverdue(t))
        .length,
    };
  });

  countFor(tab: StatusTab): number {
    return this.counts()[tab];
  }

  groups = computed<TaskGroup[]>(() => {
    const tab = this.statusTab();
    const now = this.#now();
    const buckets = new Map<GroupKey, Task[]>();

    for (const task of this.filteredTasks()) {
      const finished = isTaskFinished(task);
      // The open tab still shows what was finished today, so completing a task
      // doesn't make it vanish; older finished rows only appear under "done".
      const finishedToday = finished && this.#completedOn(task, now);
      if (
        (tab === 'open' && finished && !finishedToday) ||
        (tab === 'done' && !finished)
      ) {
        continue;
      }
      const key = !finished
        ? this.#groupFor(task, now)
        : tab === 'open'
          ? 'doneToday'
          : 'finished';
      buckets.set(key, [...(buckets.get(key) ?? []), task]);
    }

    return GROUP_ORDER.filter((key) => buckets.has(key)).map((key) => ({
      key,
      tasks: this.#sortGroup(key, buckets.get(key) ?? []),
    }));
  });

  constructor() {
    const timer = setInterval(() => this.#now.set(Date.now()), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  ngOnInit(): void {
    this.taskService.getExecutionRows();
  }

  #dayKey(date: Date | number | string): string {
    return formatDate(date, 'yyyy-MM-dd', this.#locale, TZ);
  }

  #completedOn(task: Task, day: number): boolean {
    const completedAt = task.current_execution?.completed_at;
    return !!completedAt && this.#dayKey(completedAt) === this.#dayKey(day);
  }

  #groupFor(task: Task, now: number): GroupKey {
    if (!task.due_at) return 'noDue';
    const due = new Date(task.due_at).getTime();
    if (due < now) return 'overdue';

    const day = this.#dayKey(due);
    if (day === this.#dayKey(now)) return 'today';
    if (day === this.#dayKey(now + 86_400_000)) return 'tomorrow';

    // End of the current week (Sunday), compared as Berlin calendar days.
    const weekday = WEEKDAYS.indexOf(formatDate(now, 'EEE', 'en-US', TZ)) + 1; // 1 = Monday
    const endOfWeek = this.#dayKey(now + (7 - weekday) * 86_400_000);
    return day <= endOfWeek ? 'thisWeek' : 'later';
  }

  #sortGroup(key: GroupKey, tasks: Task[]): Task[] {
    if (key === 'finished' || key === 'doneToday') {
      const completed = (t: Task) =>
        t.current_execution?.completed_at
          ? new Date(t.current_execution.completed_at).getTime()
          : 0;
      return [...tasks].sort((a, b) => completed(b) - completed(a));
    }
    const due = (t: Task) =>
      t.due_at ? new Date(t.due_at).getTime() : Number.MAX_SAFE_INTEGER;
    return [...tasks].sort((a, b) => due(a) - due(b));
  }

  dueLabel(task: Task): DueLabel {
    const dueAt = task.due_at;
    if (!dueAt) return { key: null, params: {}, text: '' };

    const time = formatDate(dueAt, 'HH:mm', this.#locale, TZ);
    if (this.isOverdue(task)) {
      return {
        key: _('task.due.overdueSince'),
        params: { date: formatDate(dueAt, 'dd.MM., HH:mm', this.#locale, TZ) },
        text: '',
      };
    }

    const now = this.#now();
    const day = this.#dayKey(dueAt);
    if (day === this.#dayKey(now)) {
      return { key: _('task.due.today'), params: { time }, text: '' };
    }
    if (day === this.#dayKey(now + 86_400_000)) {
      return { key: _('task.due.tomorrow'), params: { time }, text: '' };
    }
    return {
      key: null,
      params: {},
      text: formatDate(dueAt, 'EEE dd.MM., HH:mm', this.#locale, TZ),
    };
  }

  initials(name: string): string {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  /** Stable avatar colour per person, from a small palette of theme-safe tones. */
  avatarClass(id: number): string {
    const palette = [
      '!bg-primary-600 !text-white',
      '!bg-blue-600 !text-white',
      '!bg-violet-600 !text-white',
      '!bg-amber-600 !text-white',
      '!bg-rose-600 !text-white',
      '!bg-teal-600 !text-white',
    ];
    return palette[id % palette.length];
  }

  isOverdue(task: Task): boolean {
    return (
      !!task.due_at &&
      !isTaskFinished(task) &&
      new Date(task.due_at).getTime() < this.#now()
    );
  }

  /** Not yet overdue, but within the last stretch of its interval. */
  isDueSoon(task: Task): boolean {
    if (!task.due_at || isTaskFinished(task) || this.isOverdue(task)) {
      return false;
    }
    const remaining = new Date(task.due_at).getTime() - this.#now();
    return remaining <= getDueSoonWindowMs(task);
  }

  isFinished(task: Task): boolean {
    return isTaskFinished(task);
  }

  canComplete(task: Task): boolean {
    return task.status === 'open' || task.status === 'in_progress';
  }

  navigateToCreate() {
    this.#router.navigate(['/tasks/new']);
  }

  navigateToDetail(task: Task) {
    this.#router.navigate(['/tasks', task.id]);
  }

  /** Completes an open row, or reopens a done / accepted one. */
  toggleDone(task: Task) {
    const execution = task.current_execution;
    if (!execution) return;

    const reopen = !this.canComplete(task);
    this.taskService
      .updateExecutionStatus(task.id, execution.id, reopen ? 'open' : 'done')
      .subscribe({
        next: () => {
          this.taskService.getExecutionRows();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant(
              reopen ? 'task.reopened' : 'task.completed',
            ),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('task.updateError'),
          });
        },
      });
  }

  onDelete(event: Event, task: Task) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant('task.deleteConfirmMessage'),
      header: this.#translateService.instant('task.deleteHeader'),
      rejectLabel: this.#translateService.instant('label.cancel'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },
      accept: () => {
        this.taskService.deleteTask(task.id).subscribe({
          next: () => {
            this.taskService.getExecutionRows();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
    });
  }
}
